import React, { useState } from 'react';
import { Alert, Box, Button, Container, Grid, Paper, TextField, Typography } from '@mui/material';
import { Email, LocationOn, Phone, PersonOutline } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../services/api';
import SiteFooter from '../components/SiteFooter';

const ADDRESS = '1101, 11th Floor, Royale Avenue, Dr. Vigas Street, Chirabazar, Marine Lines, Mumbai - 400002';
const MAP_URL = 'https://maps.app.goo.gl/4t9v3iyEybXjWdkr9';

const ContactPage: React.FC = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      setLoading(true);
      await api.post('/contact-inquiries', form);
      setForm({ name: '', email: '', phone: '', message: '' });
      setSubmitted(true);
      toast.success('Your message has been sent successfully.');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Unable to send your message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: '#fdf3e4', color: '#402b24' }}>
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 7 } }}>
        <Box sx={{ background: 'linear-gradient(135deg, #7b0011 0%, #a51f32 100%)', color: '#fffaf2', borderRadius: 4, p: { xs: 3, md: 4 }, mb: 3, boxShadow: '0 14px 30px rgba(123, 0, 17, 0.18)' }}>
          <Typography sx={{ fontSize: '0.8rem', letterSpacing: 1.2, textTransform: 'uppercase', opacity: 0.8, mb: 0.5 }}>Maandvo Matrimony Service</Typography>
          <Typography variant="h3" sx={{ fontWeight: 900, fontFamily: "'Georgia', serif" }}>Contact Us</Typography>
          <Typography sx={{ mt: 0.8, color: 'rgba(255,250,242,0.88)' }}>We are here to help your family take the next step with confidence.</Typography>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Paper component="form" onSubmit={submit} elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, border: '1px solid #eddac2', background: '#fffaf2' }}>
              <Typography variant="h5" sx={{ color: '#7b0011', fontWeight: 800, mb: 2 }}>Send us a message</Typography>
              {submitted && <Alert severity="success" sx={{ mb: 2 }}>Thank you. Our team will contact you shortly.</Alert>}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><TextField required fullWidth label="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Grid>
                <Grid item xs={12} sm={6}><TextField required fullWidth type="email" label="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Grid>
                <Grid item xs={12}><TextField required fullWidth label="Mobile number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Grid>
                <Grid item xs={12}><TextField required fullWidth multiline minRows={5} label="How can we help?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></Grid>
                <Grid item xs={12}><Button type="submit" disabled={loading} variant="contained" size="large" sx={{ background: '#7b0011', '&:hover': { background: '#5d000d' } }}>{loading ? 'Sending...' : 'Send Message'}</Button></Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, border: '1px solid #eddac2', background: '#fffaf2', height: '100%' }}>
              <Typography variant="h5" sx={{ color: '#7b0011', fontWeight: 900, mb: 2 }}>Visit or call us</Typography>
              <Box sx={{ background: '#fdf3e4', borderRadius: 2, p: 2, borderLeft: '4px solid #7b0011' }}>
                <Typography sx={{ color: '#8c6a52', fontSize: '0.75rem', fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', mb: 0.6 }}>Your Matrimony Advisors</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><PersonOutline sx={{ color: '#7b0011' }} /><Typography sx={{ color: '#7b0011', fontWeight: 900, fontSize: '1.08rem' }}>Kinnari Shah</Typography></Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.8 }}><PersonOutline sx={{ color: '#7b0011' }} /><Typography sx={{ color: '#7b0011', fontWeight: 900, fontSize: '1.08rem' }}>Krishnakant Shah</Typography></Box>
              </Box>
              <Box component="a" href="tel:9725252323" sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'center', color: '#402b24', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem' }}><Phone sx={{ color: '#7b0011' }} />9725252323</Box>
              <Typography sx={{ mt: 0.8, color: '#7a5a4d', fontWeight: 700 }}>Call Timings: Mon-Sat, 11:00 AM to 6:00 PM</Typography>
              <Typography sx={{ mt: 0.2, color: '#7a5a4d' }}>Same number is available on WhatsApp.</Typography>
              <Box component="a" href="mailto:support@maandvo.com" sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center', color: '#402b24', textDecoration: 'none', fontWeight: 700 }}><Email sx={{ color: '#7b0011' }} />support@maandvo.com</Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 2, p: 1.5, alignItems: 'flex-start', background: '#fffaf2', borderRadius: 2 }}><LocationOn sx={{ color: '#7b0011' }} /><Typography sx={{ lineHeight: 1.55 }}>{ADDRESS}</Typography></Box>
              <Button component="a" href={MAP_URL} target="_blank" rel="noreferrer" startIcon={<LocationOn />} variant="outlined" sx={{ mt: 3, color: '#7b0011', borderColor: '#7b0011' }}>Open in Google Maps</Button>
              <Box component="iframe" title="Maandvo Matrimony location" src={`https://www.google.com/maps?q=${encodeURIComponent(ADDRESS)}&output=embed`} sx={{ border: 0, width: '100%', height: 240, mt: 3, borderRadius: 2 }} />
            </Paper>
          </Grid>
        </Grid>
      </Container>
      <SiteFooter />
    </Box>
  );
};

export default ContactPage;
